import React, { forwardRef, memo, useEffect, useImperativeHandle, useRef } from 'react';
import './styles.scss';

// Noise color per theme: a near-white swirl reads on a dark surface but
// disappears on a light one (and vice-versa for a dark swirl), so the
// gl_FragColor tint has to flip with the app's appearance, not just the CSS
// ring/glow around it.
const NOISE_THEME = {
    dark: {
        color1: [0.984, 0.984, 0.984], // #fbfbfb
        color2: [0.842, 0.842, 0.842], // #d6d6d6
    },
    light: {
        color1: [0.984, 0.984, 0.984], // #fbfbfb
        color2: [0.842, 0.842, 0.842], // #d6d6d6
    },
};

const LERP = 0.08; // per-frame ease, matches GlobeBackground's theme cross-fade

// The orbit (dotted ring of small orbs spinning around the sphere) is off for now;
// flip to bring it back — its styles are still in styles.scss.
const ORBIT = false;

// How long the canvas keeps drawing after deactivation: long enough for the
// CSS fade (styles.scss, 0.45s) to finish on a still-moving swirl.
const IDLE_DRAW_MS = 600;

function currentAppearance() {
    if (typeof window !== 'undefined' && window.appearenceMode) {
        return window.appearenceMode === 'dark' ? 'dark' : 'light';
    }
    if (typeof document !== 'undefined' && document.body.classList.contains('dark')) {
        return 'dark';
    }
    return 'light';
}

/**
 * AICircle — the animated "AI" orb.
 *
 * Two states. Active (default): the noise swirl plays, the orbit spins and
 * the glow pulses. Inactive: the swirl fades out and its render loop stops,
 * the orbit tucks behind the sphere and the glow dismisses — a calm sphere.
 * Both directions animate.
 *
 * The state is imperative on purpose: toggling it through the ref flips a
 * class on the DOM node and pauses/resumes the WebGL loop without a React
 * re-render (the parent may re-render freely; `memo` keeps this subtree
 * still). `active` only seeds the initial state.
 *
 *   const orb = useRef();
 *   <AICircle ref={orb} size={20} active={false} />
 *   orb.current.setActive(true);
 *
 * @param {number}  [size=28]     diameter in px
 * @param {boolean} [active=true] initial state
 */
const AICircle = memo(forwardRef(function AICircle({ size = 28, active = true }, ref) {
    const canvasRef = useRef(null);
    const rootRef = useRef(null);
    const activeRef = useRef(active);
    // The running loop's "start" hook, so setActive(true) can resume a loop
    // that stopped after the deactivation fade.
    const resumeRef = useRef(null);

    useImperativeHandle(ref, () => ({
        setActive(next) {
            const value = !!next;
            if (activeRef.current === value) return;
            activeRef.current = value;
            rootRef.current?.classList.toggle('is-inactive', !value);
            if (value) resumeRef.current?.();
        },
        isActive() {
            return activeRef.current;
        },
    }), []);

    useEffect(() => {
        const canvasEl = canvasRef.current;
        if (!canvasEl) return;

        const gl = canvasEl.getContext("webgl") || canvasEl.getContext("experimental-webgl");
        if (!gl) {
            console.error("WebGL is not supported by your browser.");
            return;
        }

        let animationFrameId = null;
        const devicePixelRatio = Math.min(window.devicePixelRatio, 2);

        // Vertex Shader
        const vsSource = `
            precision mediump float;
            varying vec2 vUv;
            attribute vec2 a_position;

            void main() {
                vUv = .5 * (a_position + 1.);
                gl_Position = vec4(a_position, 0.0, 1.0);
            }
        `;

        // Fragment Shader
        const fsSource = `
            precision mediump float;

            varying vec2 vUv;
            uniform float u_time;
            uniform float u_ratio;
            uniform vec2 u_pointer_position;
            uniform float u_scroll_progress;
            uniform vec3 u_color1;
            uniform vec3 u_color2;

            vec2 rotate(vec2 uv, float th) {
                return mat2(cos(th), sin(th), -sin(th), cos(th)) * uv;
            }

            float neuro_shape(vec2 uv, float t, float p) {
                vec2 sine_acc = vec2(0.);
                vec2 res = vec2(0.);
                float scale = 8.;

                for (int j = 0; j < 15; j++) {
                    uv = rotate(uv, 1.);
                    sine_acc = rotate(sine_acc, 1.);
                    vec2 layer = uv * scale + float(j) + sine_acc - t;
                    sine_acc += sin(layer) + 2.4 * p;
                    res += (.5 + .5 * cos(layer)) / scale;
                    scale *= (1.2);
                }
                return res.x + res.y;
            }

            void main() {
                vec2 uv = .5 * vUv;
                uv.x *= u_ratio;

                vec2 pointer = vUv - u_pointer_position;
                pointer.x *= u_ratio;
                float p = clamp(length(pointer), 0., 1.);
                p = .5 * pow(1. - p, 2.);

                float t = .001 * u_time;
                vec3 color = vec3(0.);

                float noise = neuro_shape(uv, t, p);

                noise = 1.5 * pow(noise, 2.); // Denser (lower power, higher multiplier)
                noise += pow(noise, 10.);
                noise = max(.0, noise - .5);

                // --- Animation Logic ---
                // Cycle every 6 seconds
                float cycle_len = 6.0;
                float periodic_time = mod(t, cycle_len);

                // Event Timeline:
                // 1.0 - 1.8: Concentration (0.8s)
                // 1.8 - 3.8: Rotation (2.0s)
                // 3.8 - 4.6: Dissipation (0.8s)

                float t_conc_start = 1.0;
                float t_rot_start = 1.8;
                float t_diss_start = 3.8;
                float t_end = 4.6;

                // Calculate state drivers
                // Use smoother interpolation for transitions
                float conc_progress = smoothstep(t_conc_start, t_rot_start, periodic_time);
                float diss_progress = smoothstep(t_diss_start, t_end, periodic_time);

                // active_factor rises to 1 then falls to 0
                float active_factor = conc_progress - diss_progress;
                active_factor = clamp(active_factor, 0.0, 1.0);
                // smoother mix curve
                active_factor = smoothstep(0.0, 1.0, active_factor);

                // Rotation progress (linear is fine for rotation, but we clamp it)
                float rot_progress = (periodic_time - t_rot_start) / (t_diss_start - t_rot_start);
                rot_progress = clamp(rot_progress, 0.0, 1.0);

                // Base Center Mask: Normal state
                // Since canvas is 140% of the 78px circle, the visual border is at dist = 0.5 / 1.4 = ~0.357
                float dist = length(vUv - .5);

                // Scale helper: 1.0 at center, 0.0 at visual border (0.357)
                float visual_border = 0.357;

                // Normal center mask fades out before the visual border
                float center_mask = 1.0 - smoothstep(0.0, visual_border, dist);

                // Ring Mask: Positioned AT and slightly OUTSIDE the visual border
                // visual_border is ~0.36. We want it to start inside and go out.
                // Inner edge: 0.28 (inside circle)
                // Peak: 0.36 (border)
                // Outer edge: 0.45 (outside - escaping!)
                float ring_mask = smoothstep(0.28, 0.36, dist) * (1.0 - smoothstep(0.36, 0.48, dist));

                // Rotational Mask with softer tail
                float angle = atan(vUv.y - 0.5, vUv.x - 0.5);
                float target_angle_rad = (rot_progress * 6.28318) - 3.14159;
                float angle_match = cos(angle - target_angle_rad);

                // rot_mask: Make it very smooth
                float rot_mask = smoothstep(-1.0, 1.0, angle_match);

                // Refined mixing:
                float final_mask = mix(center_mask, ring_mask * rot_mask * 3.5, active_factor);

                noise *= final_mask;

                // Mix colors based on time/scroll to create the gradient effect
                float mixVal = .5 + .5 * sin(3. * u_scroll_progress + u_time * 0.001);
                color = mix(u_color1, u_color2, mixVal);

                color = color * noise;

                gl_FragColor = vec4(color, noise);
            }
        `;

        function createShader(gl, sourceCode, type) {
            const shader = gl.createShader(type);
            gl.shaderSource(shader, sourceCode);
            gl.compileShader(shader);

            if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
                console.error("An error occurred compiling the shaders: " + gl.getShaderInfoLog(shader));
                gl.deleteShader(shader);
                return null;
            }
            return shader;
        }

        const vertexShader = createShader(gl, vsSource, gl.VERTEX_SHADER);
        const fragmentShader = createShader(gl, fsSource, gl.FRAGMENT_SHADER);

        if (!vertexShader || !fragmentShader) return;

        function createShaderProgram(gl, vertexShader, fragmentShader) {
            const program = gl.createProgram();
            gl.attachShader(program, vertexShader);
            gl.attachShader(program, fragmentShader);
            gl.linkProgram(program);

            if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
                console.error("Unable to initialize the shader program: " + gl.getProgramInfoLog(program));
                return null;
            }
            return program;
        }

        const shaderProgram = createShaderProgram(gl, vertexShader, fragmentShader);
        if (!shaderProgram) return;

        function getUniforms(program) {
            let uniforms = {};
            let uniformCount = gl.getProgramParameter(program, gl.ACTIVE_UNIFORMS);
            for (let i = 0; i < uniformCount; i++) {
                let uniformName = gl.getActiveUniform(program, i).name;
                uniforms[uniformName] = gl.getUniformLocation(program, uniformName);
            }
            return uniforms;
        }

        const uniforms = getUniforms(shaderProgram);

        const vertices = new Float32Array([-1., -1., 1., -1., -1., 1., 1., 1.]);
        const vertexBuffer = gl.createBuffer();
        gl.bindBuffer(gl.ARRAY_BUFFER, vertexBuffer);
        gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STATIC_DRAW);

        gl.useProgram(shaderProgram);

        const positionLocation = gl.getAttribLocation(shaderProgram, "a_position");
        gl.enableVertexAttribArray(positionLocation);
        gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0);

        const resizeCanvas = () => {
            // Use the parent container size or informed size
            const width = canvasEl.clientWidth || size;
            const height = canvasEl.clientHeight || size;

            canvasEl.width = width * devicePixelRatio;
            canvasEl.height = height * devicePixelRatio;

            gl.uniform1f(uniforms.u_ratio, width / height);
            gl.viewport(0, 0, canvasEl.width, canvasEl.height);
        };

        resizeCanvas();

        // Animation logic
        const pointer = { x: 0.5, y: 0.5 }; // normalized center

        // We simulate a bit of automatic pointer movement or just keep it centered
        // The user said "Does not need to be responsive on hover, just make the animation automatic."
        // We rely on u_time for the main noise animation.
        // We can pass a fake scroll progress to cycle colors slowly.

        // `live` is what we render; `target` is where the current theme wants it, so a
        // theme flip cross-fades the noise tint instead of snapping (see GlobeBackground).
        const target = {
            color1: [...NOISE_THEME[currentAppearance()].color1],
            color2: [...NOISE_THEME[currentAppearance()].color2],
        };
        const live = {
            color1: [...target.color1],
            color2: [...target.color2],
        };

        const lerp = (a, b) => a + (b - a) * LERP;
        const lerpInto = (arr, dest) => {
            for (let i = 0; i < arr.length; i += 1) arr[i] = lerp(arr[i], dest[i]);
        };

        const onAppearance = () => {
            const theme = NOISE_THEME[currentAppearance()];
            target.color1 = [...theme.color1];
            target.color2 = [...theme.color2];
        };
        window.addEventListener('appearence-change', onAppearance);

        // Inactive orbs stop drawing once their canvas has faded out, so a
        // thread full of settled answers costs no GPU time.
        let idleSince = 0;

        const render = () => {
            const currentTime = performance.now();

            // Cycle colors slowly with time instead of scroll
            const fakeScroll = currentTime * 0.0001;

            lerpInto(live.color1, target.color1);
            lerpInto(live.color2, target.color2);

            gl.uniform1f(uniforms.u_time, currentTime);
            // Center the "pointer" effect
            gl.uniform2f(uniforms.u_pointer_position, pointer.x, pointer.y);
            gl.uniform1f(uniforms.u_scroll_progress, fakeScroll);
            gl.uniform3f(uniforms.u_color1, live.color1[0], live.color1[1], live.color1[2]);
            gl.uniform3f(uniforms.u_color2, live.color2[0], live.color2[1], live.color2[2]);

            gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);

            if (!activeRef.current) {
                if (!idleSince) idleSince = currentTime;
                if (currentTime - idleSince > IDLE_DRAW_MS) {
                    animationFrameId = null;
                    return;
                }
            } else {
                idleSince = 0;
            }

            animationFrameId = requestAnimationFrame(render);
        };

        const resume = () => {
            idleSince = 0;
            if (animationFrameId === null) animationFrameId = requestAnimationFrame(render);
        };
        resumeRef.current = resume;
        resume();

        return () => {
            if (animationFrameId !== null) cancelAnimationFrame(animationFrameId);
            animationFrameId = null;
            resumeRef.current = null;
            window.removeEventListener('appearence-change', onAppearance);
            gl.deleteProgram(shaderProgram);
        };
    }, [size]);

    return (
        <div
            ref={rootRef}
            className={`ai-circle-container ${activeRef.current ? '' : 'is-inactive'}`.trim()}
            style={{ '--size': `${size}px` }}
        >
            <div className="ai-circle-glow" />
            <div className="ai-circle-visual" />
            <canvas ref={canvasRef} id="neuro" />
            {/* The layer tucks/reveals the orbit; the container inside spins. */}
            {ORBIT && (
                <div className="orbit-layer">
                    <div className="orbit-container">
                        {Array.from({ length: 11 }).map((_, i) => (
                            <div key={i} className="orbit-ball" style={{ '--i': i }} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}));

export default AICircle;
